import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Audit } from '../models/audit.model';
import { environment } from '../environments/environment';
import { QuestionItem } from '../models/question-item.model';
import { map, Observable, switchMap, tap } from 'rxjs';
import { FiveSArea } from '../models/five-s-area.interface';
import { AdhocAuditRequest } from '../models/adhoc-audit-request.model';
import { AuditState } from '../models/audit-state.enum';
import { SettingsService } from './settings.service';


@Injectable({ providedIn: 'root' })
export class AssignmentService {
  public auditInProgress = computed(() => this._assignments().filter(a => a.state === AuditState.Started && this.notOverdue(a)));
  public upcomingAudits = computed(() => this._assignments().filter(a => a.state === AuditState.NotStarted && this.notOverdue(a)).sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime()));
  public finishedAudits = computed(() => this._assignments().filter(a => a.state === AuditState.Finished).sort((a, b) => b.dueDate.getTime() - a.dueDate.getTime()));
  public assignments = computed(() => this._assignments());
  private _assignments: WritableSignal<Audit[]> = signal([]);
  private baseUrl = environment.serverUrl + '/api/Assignment';

  private readonly http = inject(HttpClient);
  private readonly settingsService = inject(SettingsService)

  constructor() {
    this.getAssignments().subscribe();
  }

  public getAssignments(): Observable<void> {
    return this.http
      .get<Audit[]>(`${ this.baseUrl }/Assignments`).pipe(
        tap(assignments => {
          assignments.forEach(element => element.dueDate = new Date(element.dueDate));
          this._assignments.set([...assignments])
        }),
        map(() => undefined)
      )
  }

  public getTaskList(taskLayoutId: string): Observable<QuestionItem[]> {
    let langCode = this.settingsService.currentSettings.language.selectedValue.value
    return this.http.get<QuestionItem[]>(`${ this.baseUrl }/StartAudit/${ taskLayoutId }?lang=${ langCode }`);
  }

  public getTaskLayoutId(selectedScheduleGuid?: string): Observable<string> {
    return this.http.get<string>(`${ this.baseUrl }/CreateTaskLayoutForSchedule/${ selectedScheduleGuid }`).pipe(
      tap(() => this.getAssignments())
    );
  }

  public getTaskLayoutIdForAdhoc(data: AdhocAuditRequest): Observable<string> {
    return this.http.get<string>(`${ this.baseUrl }/CreateTaskLayoutForAdhocAudit`, { params: data }).pipe(
      tap(() => this.getAssignments())
    );
  }

  public taskLayoutIsFinished(taskLayoutId?: string): Observable<boolean> {
    return this.http.get<boolean>(`${ this.baseUrl }/taskLayoutIsFinished/${ taskLayoutId }`);
  }

  public finishTaskLayout(taskLayoutId?: string): Observable<void> {
    return this.http.patch<void>(`${ this.baseUrl }/taskLayoutIsFinished/${ taskLayoutId }`, null).pipe(
      switchMap(() => this.getAssignments())
    );
  }

  public getAreas(): Observable<FiveSArea[]> {
    return this.http.get<FiveSArea[]>(`${ this.baseUrl }/FiveSAreas`);
  }

  public saveQuestionResult(data: QuestionItem): Observable<void> {
    data.comment ??= '';
    return this.http.patch<void>(`${ this.baseUrl }/SaveQuestion`, data)
  }

  public duplicateQuestionTask(taskId: string) {
    const header = new HttpHeaders({ 'content-type': 'application/json' });
    return this.http.post<void>(`${this.baseUrl }/DuplicateQuestion`, JSON.stringify(taskId), { headers: header });
  }

  public isOverdue(taskLayoutId: string): boolean {
    let audit = this._assignments().find(a => a.layoutId === taskLayoutId);
    if (audit) {
      return !this.notOverdue(audit);
    }
    return true
  }

  private notOverdue(audit: Audit): boolean {

    let todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    return audit.dueDate.getTime() >= todayDate.getTime();
  }
}
